import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, ArrowLeft, ArrowRight, Play, Pause, Timer,
  MessageCircle, ChevronLeft, Maximize2, Minimize2, X, Home
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LecturePresentation() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(900);
  const [timerRunning, setTimerRunning] = useState(false);
  const [showDiscussion, setShowDiscussion] = useState(false);
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  const lectures = unit?.lecture_notes?.mini_lectures || [];
  const totalSlides = lectures.length + 2;

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => Math.min(prev + 1, totalSlides - 1));
    setShowDiscussion(false);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
    setShowDiscussion(false);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/units/${unitId}`);
        setUnit(res.data);
      } catch {
        toast.error('Failed to load unit');
      }
    };
    fetchData();
  }, [unitId]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); nextSlide(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prevSlide(); }
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [nextSlide, prevSlide]);

  useEffect(() => {
    if (timerRunning && timerSeconds > 0) {
      timerRef.current = setInterval(() => {
        setTimerSeconds(prev => {
          if (prev <= 1) {
            setTimerRunning(false);
            toast.info('Timer complete! Time for discussion.');
            setShowDiscussion(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [timerRunning, timerSeconds]);

  const resetTimer = (mins = 15) => {
    setTimerSeconds(mins * 60);
    setTimerRunning(false);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  if (!unit) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent" /></div>;

  const renderSlide = () => {
    if (currentSlide === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <BookOpen className="h-16 w-16 text-slate-600 mb-6" strokeWidth={1} />
          <h1 className="text-4xl sm:text-5xl font-serif font-light text-[#14213D] mb-4">{unit.title}</h1>
          <p className="text-lg text-[#5A6577] mb-2">Grade {unit.grade_level} &middot; Unit {unit.unit_number}</p>
          <p className="text-base text-[#5A6577] max-w-2xl">{unit.lecture_notes?.total_duration || '60 minutes'}</p>
          <div className="mt-8 flex gap-2">
            <Button onClick={nextSlide} className="btn-primary">Begin Lecture <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </div>
        </div>
      );
    }

    if (currentSlide === totalSlides - 1) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <h2 className="text-3xl font-serif text-[#14213D] mb-6">Lecture Summary</h2>
          <div className="max-w-2xl text-left space-y-3">
            {lectures.map((l, i) => (
              <div key={`lec-${l.lecture_number || i}-${l.title?.slice(0,20) || ''}`} className="flex items-start gap-3">
                <span className="text-yellow-600 font-bold text-lg">{i + 1}.</span>
                <div>
                  <h3 className="font-medium text-[#14213D]">{l.title}</h3>
                  <p className="text-sm text-[#5A6577]">{l.overview}</p>
                </div>
              </div>
            ))}
          </div>
          {unit.lecture_notes?.assessment_suggestions?.length > 0 && (
            <div className="mt-6 max-w-2xl text-left">
              <h3 className="font-medium text-[#14213D] mb-2">Assessment Ideas:</h3>
              <ul className="text-sm text-[#5A6577] space-y-1">
                {unit.lecture_notes.assessment_suggestions.map((a, i) => <li key={`as-${i}-${a.slice(0,30)}`}>&bull; {a}</li>)}
              </ul>
            </div>
          )}
        </div>
      );
    }

    const lecture = lectures[currentSlide - 1];
    if (!lecture) return null;

    return (
      <div className="h-full overflow-y-auto px-6 sm:px-12 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="bg-[#D4AF37] text-[#14213D] text-sm font-bold px-3 py-1 rounded">Lecture {lecture.lecture_number}</span>
            <span className="text-sm text-[#5A6577]">{lecture.duration}</span>
          </div>
          <h2 className="text-3xl font-serif text-[#14213D] mb-4">{lecture.title}</h2>
          <p className="text-[#5A6577] mb-6 text-base">{lecture.overview}</p>

          <div className="space-y-3 mb-8">
            <h3 className="text-sm font-medium uppercase tracking-wide text-[#5A6577]">Key Points</h3>
            {lecture.key_points?.map((point, i) => (
              <div key={`kp-${i}-${point.slice(0,30)}`} className="flex items-start gap-3 bg-white/50 rounded-lg p-3">
                <span className="text-yellow-600 font-bold mt-0.5">&bull;</span>
                <p className="text-[#14213D]">{point}</p>
              </div>
            ))}
          </div>

          {lecture.transition_note && (
            <div className="bg-[#F9F8F4] border-l-4 border-yellow-500 p-4 rounded-r">
              <p className="text-sm text-[#5A6577] italic">{lecture.transition_note}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const currentLecture = currentSlide > 0 && currentSlide < totalSlides - 1 ? lectures[currentSlide - 1] : null;

  return (
    <div ref={containerRef} className={`${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'} bg-[#FDFBF7] flex flex-col`}>
      {/* Top Bar */}
      <div className="glass-nav px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/lecture-notes/${unitId}`)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-[#5A6577] truncate max-w-xs">{unit.title}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setShowTimer(!showTimer)}>
              <Timer className="h-4 w-4" />
            </Button>
            {showTimer && (
              <div className="flex items-center gap-1 bg-white rounded-lg px-2 py-1 shadow-sm">
                <span className={`font-mono text-sm font-bold ${timerSeconds < 60 ? 'text-red-600' : 'text-[#14213D]'}`}>
                  {formatTime(timerSeconds)}
                </span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setTimerRunning(!timerRunning)}>
                  {timerRunning ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                </Button>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => resetTimer(15)}>
                  <span className="text-[11px]">15m</span>
                </Button>
              </div>
            )}
          </div>
          {currentLecture && (
            <Button variant="ghost" size="sm" onClick={() => setShowDiscussion(!showDiscussion)}>
              <MessageCircle className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={toggleFullscreen}>
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Slide Content */}
      <div className="flex-1 relative">
        {renderSlide()}

        {showDiscussion && currentLecture?.discussion_questions && (
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white border-l shadow-lg p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-[#14213D]">Discussion</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowDiscussion(false)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-4">
              {currentLecture.discussion_questions.map((dq, i) => (
                <Card key={`dq-${i}-${(dq.question || dq).slice(0,30)}`} className="p-3">
                  <p className="text-sm font-medium text-[#14213D] mb-1">{dq.question || dq}</p>
                  {dq.purpose && <p className="text-xs text-[#5A6577]">{dq.purpose}</p>}
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Nav */}
      <div className="glass-nav px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={prevSlide} disabled={currentSlide === 0}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#5A6577]">{currentSlide + 1} / {totalSlides}</span>
            <Progress value={((currentSlide + 1) / totalSlides) * 100} className="w-32 h-1.5" />
          </div>
          <Button variant="ghost" size="sm" onClick={nextSlide} disabled={currentSlide === totalSlides - 1}>
            Next <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
